import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui';
import BaseInput from '../ui/BaseInput';
import MaskPasswordButton from '../ui/shared/MaskPasswordButton';
import { useLanguage } from '@/contexts';
import { usePasswordDisplay } from '@/hooks';
import { ThemeToggle, LanguageSwitcher } from '@/components/ui';
import { ArrowLeftIcon, CheckCircleIcon, KeyIcon, ShieldCheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

import { API_URLS } from '../../constants';

// Constants
const OTP_LENGTH = 6;
const MIN_PASSWORD_LENGTH = 8;
const RESEND_COOLDOWN_SECONDS = 30;
const PASSWORD_STRENGTH_LEVELS = 5;

interface ForgotPasswordFormData {
  email: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
}

type Step = 0 | 1 | 2 | 3;

const ForgotPasswordPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { showPassword, setShowPassword } = usePasswordDisplay();
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState<Step>(0);
  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_URLS.AUTH.FORGOT_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });

      if (response.ok) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setStep(1);
      } else {
        const errorData = await response.json();
        setError(errorData.message || t('unexpectedError'));
      }
    } catch (err) {
      setError(t('networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.otp.length !== OTP_LENGTH) {
      setError(`Enter the ${OTP_LENGTH}-digit code from your email.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_URLS.AUTH.VERIFY_RESET_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email, otp: formData.otp }),
      });

      if (response.ok) {
        setStep(2);
      } else {
        const errorData = await response.json();
        setError(errorData.error === 'invalid_code' ? 'Invalid code. Please try again.' : 
                errorData.error === 'expired_code' ? 'Code expired. Tap Resend.' : 
                errorData.message || 'Invalid or expired code. Try again or resend.');
      }
    } catch (err) {
      setError(t('networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords don\'t match.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_URLS.AUTH.RESET_PASSWORD_CONFIRM, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: formData.email, 
          otp: formData.otp, 
          newPassword: formData.newPassword 
        }),
      });

      if (response.ok) {
        setStep(3);
      } else {
        const errorData = await response.json();
        
        // Handle specific error types with user-friendly messages
        switch (errorData.error) {
          case 'same_password':
            setError('🔄 You cannot use your current password. Please choose a different password.');
            break;
          case 'weak_password':
            setError('🔒 Password must be at least 8 characters long.');
            break;
          case 'invalid_code':
            setError('❌ Invalid verification code. Please check your code and try again.');
            break;
          case 'expired_code':
            setError('⏰ Your verification code has expired. Please request a new one.');
            break;
          case 'used_code':
            setError('🔄 This code has already been used. Please request a new one.');
            break;
          case 'invalid_or_expired':
            setError('⚠️ Your reset session has expired. Please start over.');
            break;
          default:
            setError(errorData.message || '❌ Failed to update password. Please try again.');
        }
      }
    } catch (err) {
      setError('🌐 Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_URLS.AUTH.RESEND_RESET_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });

      if (response.ok) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setFormData(prev => ({ ...prev, otp: '' }));
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to resend code.');
      }
    } catch (err) {
      setError(t('networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof ForgotPasswordFormData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleBack = () => {
    if (step > 0) {
      setStep((step - 1) as Step);
      setError(null);
    }
  };

  const getPasswordStrength = (password: string) => {
    let score = 0;
    if (password.length >= MIN_PASSWORD_LENGTH) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return Math.min(score, PASSWORD_STRENGTH_LEVELS);
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);
  const strengthLabels = ['Very weak', 'Weak', 'Ok', 'Good', 'Strong'];
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];

  // Step progress indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center space-x-2 mb-6">
      {[
        { icon: ArrowRightIcon, label: 'Email', step: 0 },
        { icon: KeyIcon, label: 'OTP', step: 1 },
        { icon: ShieldCheckIcon, label: 'Password', step: 2 },
        { icon: CheckCircleIcon, label: 'Done', step: 3 },
      ].map(({ icon: Icon, label, step: stepNum }) => (
        <div key={stepNum} className="flex items-center">
          <div
            className={`flex items-center justify-center w-7 h-7 rounded-full border-2 ${
              step > stepNum
                ? 'bg-green-100 border-green-300'
                : step === stepNum
                ? 'bg-brand-electric-blue border-brand-electric-blue'
                : 'bg-white border-gray-200'
            }`}
          >
            {step > stepNum ? (
              <CheckCircleIcon className="w-4 h-4 text-green-600" />
            ) : (
              <Icon className={`w-4 h-4 ${step === stepNum ? 'text-white' : 'text-gray-400'}`} />
            )}
          </div>
          <span className={`ml-1 text-xs ${step === stepNum ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
            {label}
          </span>
          {stepNum < 3 && <div className="w-6 h-px bg-gray-200 mx-1" />}
        </div>
      ))}
    </div>
  );

  // OTP Input Component - Simple and reliable approach
  const OtpInput = () => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH);
      setFormData(prev => ({ ...prev, otp: value }));
      if (error) setError(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow backspace, delete, arrow keys, tab, ctrl+v, cmd+v
      if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        return; // Allow paste
      }
      // Only allow digits
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    };

    return (
      <div className="space-y-4">
        {/* Simple input field with OTP styling */}
        <div className="flex justify-center">
          <input
            type="text"
            inputMode="numeric"
            value={formData.otp}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            maxLength={OTP_LENGTH}
            autoComplete="one-time-code"
            autoFocus
            placeholder="000000"
            className="w-48 h-16 text-center text-3xl font-bold border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-brand-electric-blue/20 focus:border-brand-electric-blue transition-all duration-200 bg-white shadow-sm hover:shadow-md tracking-widest"
            aria-label={`${OTP_LENGTH}-digit verification code`}
          />
        </div>
        
        {/* Helper text */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            Type or paste your {OTP_LENGTH}-digit code
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors duration-300 flex flex-col">
      {/* Top Bar with Theme and Language Toggles */}
      <div className="w-full bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 transition-colors duration-300">
        <div className="container mx-auto px-4 py-3 flex justify-end gap-2 sm:gap-3">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-xl">
          <div className="bg-white dark:bg-slate-800 py-10 px-6 shadow-lg dark:shadow-lg dark:shadow-slate-900/50 sm:rounded-xl sm:px-14 border border-gray-200 dark:border-slate-700 transition-colors duration-300">
          {/* Header with back button and progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              {step === 0 ? (
                <button
                  onClick={() => navigate('/auth/login')}
                  className="flex items-center text-gray-600 hover:text-gray-900 p-2 rounded-md hover:bg-gray-100"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  {t('backToLogin')}
                </button>
              ) : step < 3 ? (
                <button
                  onClick={handleBack}
                  className="flex items-center text-gray-600 hover:text-gray-900 p-2 rounded-md hover:bg-gray-100"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Back
                </button>
              ) : null}
            </div>
            
            <StepIndicator />
            
            <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-slate-100 transition-colors duration-300">
              {step === 0 && t('forgotYourPassword')}
              {step === 1 && 'Enter verification code'}
              {step === 2 && 'Create new password'}
              {step === 3 && 'Password updated'}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-slate-400 transition-colors duration-300">
              {step === 0 && t('enterYourEmailAddress')}
              {step === 1 && `We sent a code to ${formData.email}`}
              {step === 2 && 'Choose a strong password for your account'}
              {step === 3 && 'You can now log in with your new credentials'}
            </p>
          </div>

          {/* Step 0: Email Input */}
          {step === 0 && (
            <form onSubmit={handleSendOtp} className="space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm text-center">
                  {error}
                </div>
              )}

              <BaseInput
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
                autoComplete="email"
                placeholder={t('enterYourEmailAddress')}
              />

              <Button
                type="submit"
                loading={loading}
                disabled={!formData.email || loading}
                className="w-full py-3 bg-brand-dark-blue hover:bg-brand-electric-blue text-white font-medium rounded-md"
              >
                Send OTP
              </Button>
            </form>
          )}

          {/* Step 1: OTP Verification */}
          {step === 1 && (
            <form onSubmit={handleVerifyOtp} className="space-y-8">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm text-center">
                  {error}
                </div>
              )}

              <div className="space-y-6">
                <label className="block text-sm font-medium text-gray-700 text-center">
                  {OTP_LENGTH}-digit verification code
                </label>
                <OtpInput />
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setStep(0)}
                  className="text-sm text-brand-electric-blue hover:text-brand-dark-blue font-medium"
                >
                  Change email
                </button>
                <Button
                  type="submit"
                  loading={loading}
                  disabled={formData.otp.length !== OTP_LENGTH || loading}
                  className="px-8 py-3 bg-brand-dark-blue hover:bg-brand-electric-blue text-white font-medium rounded-md"
                >
                  Verify code
                </Button>
              </div>

              <div className="text-center">
                {resendCooldown > 0 ? (
                  <span className="text-sm text-gray-600">
                    Resend available in {resendCooldown}s
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    className="text-sm text-brand-electric-blue hover:text-brand-dark-blue font-medium"
                  >
                    Resend code
                  </button>
                )}
              </div>
            </form>
          )}

          {/* Step 2: New Password */}
          {step === 2 && (
            <form onSubmit={handleUpdatePassword} className="space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm text-center">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <BaseInput
                    label="New Password"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={handleChange('newPassword')}
                    required
                    autoComplete="new-password"
                    placeholder="Enter new password"
                    rightAddon={
                      <MaskPasswordButton 
                        showPassword={showPassword} 
                        setShowPassword={setShowPassword} 
                      />
                    }
                  />
                  {formData.newPassword && (
                    <div className="mt-2">
                      <div className="h-2 w-full bg-gray-100 rounded">
                        <div
                          className={`h-2 rounded transition-all ${strengthColors[Math.max(0, passwordStrength - 1)]}`}
                          style={{ width: `${(passwordStrength / PASSWORD_STRENGTH_LEVELS) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        Strength: {strengthLabels[Math.max(0, passwordStrength - 1)] || 'Very weak'}
                      </p>
                    </div>
                  )}
                </div>

                <BaseInput
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  required
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  rightAddon={
                    <MaskPasswordButton 
                      showPassword={showConfirmPassword} 
                      setShowPassword={setShowConfirmPassword} 
                    />
                  }
                />
                {formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                  <p className="text-sm text-red-600">{t("Passwords don't match")}</p>
                )}
              </div>

              <Button
                type="submit"
                loading={loading}
                disabled={
                  !formData.newPassword ||
                  formData.newPassword.length < MIN_PASSWORD_LENGTH ||
                  formData.newPassword !== formData.confirmPassword ||
                  loading
                }
                className="w-full py-3 bg-brand-dark-blue hover:bg-brand-electric-blue text-white font-medium rounded-md"
              >
                Update password
              </Button>
            </form>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="text-center space-y-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircleIcon className="h-7 w-7 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Password updated</h3>
                <p className="text-sm text-gray-600 mt-2">
                  You can now log in with your new credentials.
                </p>
              </div>
              <Button
                onClick={() => navigate('/auth/login')}
                className="w-full py-3 bg-brand-dark-blue hover:bg-brand-electric-blue text-white font-medium rounded-md"
              >
                Go to login
              </Button>
            </div>
          )}

          {/* Footer */}
          {step < 3 && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {t('rememberYourPassword')}{' '}
                <button
                  onClick={() => navigate('/auth/login')}
                  className="text-brand-electric-blue hover:text-brand-dark-blue font-medium"
                >
                  {t('signIn')}
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default ForgotPasswordPage;
